import React from "react";
import PropTypes from "prop-types";
import { Box, Grid, Typography, Button, Collapse } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import AnimatedCounter from "./AnimatedCounter";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
});

const CountersGroup = ({ title, total, municipios, pdf, coordenadas, mapId }) => {
  const [showPDF, setShowPDF] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const mapRef = useRef(null); // Referencia para evitar re-crear el mapa

  const pdfUrl = pdf?.fields?.file?.url ? `https:${pdf.fields.file.url}` : null;

  useEffect(() => {
    if (showMap && coordenadas?.length > 0) {
      // Delay map initialization to ensure the container is in the DOM
      setTimeout(() => {
        const mapContainer = document.getElementById(mapId);
        if (!mapContainer) {
          console.error(`Map container with ID '${mapId}' not found.`);
          return;
        }

        if (mapContainer._leaflet_id) {
          // Remove the existing map instance
          mapContainer._leaflet_id = null;
        }

        if (mapRef.current) {
          mapRef.current.off();
          mapRef.current.remove();
          mapRef.current = null;
        }

        // Initialize the map
        const map = L.map(mapId, {
          center: [coordenadas[0].latitude, coordenadas[0].longitude],
          zoom: 0,
          scrollWheelZoom: false,
        });

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map);

        const bounds = [];

        coordenadas.forEach((coord) => {
          if (coord.latitude && coord.longitude) {
            L.marker([coord.latitude, coord.longitude])
              .addTo(map)
              .bindPopup(coord.name || "Ubicación");
            bounds.push([coord.latitude, coord.longitude]);
          }
        });

        if (bounds.length > 1) {
          map.fitBounds(bounds, { padding: [50, 50] });
        }

        setTimeout(() => {
          map.invalidateSize();
        }, 300);

        mapRef.current = map;
      }, 0); // Delay to ensure the container is rendered
    } else if (!showMap && mapRef.current) {
      // Remove the map instance when the map is closed
      mapRef.current.off();
      mapRef.current.remove();
      mapRef.current = null;
    }
  }, [showMap, coordenadas]);

  return (
    <Box
      sx={(theme) => ({
        textAlign: "center",
        py: 6,
        px: 2,
        position: "relative",
        overflow: "hidden",
        backgroundColor: theme.palette.counters.main,

        [theme.breakpoints.up("md")]: {
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-150%",
            width: "300%",
            height: "100%",
            background: `linear-gradient(
              120deg,
              transparent 0%,
              rgba(255,255,255,0.03) 35%,
              rgba(255,255,255,0.15) 50%,
              rgba(255,255,255,0.03) 65%,
              transparent 100%
            )`,
            animation: "shine-bg 5s linear infinite",
            pointerEvents: "none",
          },
        },

        "@keyframes shine-bg": {
          "0%": { left: "-150%" },
          "100%": { left: "100%" },
        },
      })}
    >
      <Typography variant="h4" mb={3} color="primary.contrastText">
        {title}
      </Typography>

      <Grid
        container
        spacing={5}
        justifyContent="center"
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Grid item>
          <AnimatedCounter end={total} label="Total" />
        </Grid>
        {municipios.map((muni, index) => (
          <Grid item key={index}>
            <AnimatedCounter end={muni.cantidad} label={muni.nombre} />
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 4,
          flexWrap: "wrap",
        }}
      >
        {pdfUrl && (
          <>
            <Button
              onClick={() => setShowPDF((prev) => !prev)}
              variant="outlined"
              color="white"
            >
              {showPDF ? "Ocultar documento" : "Ver documento"}
            </Button>

            <Button
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="white"
            >
              Descargar PDF
            </Button>
          </>
        )}

        {coordenadas?.length > 0 && (
          <Button
            onClick={() => setShowMap((prev) => !prev)}
            variant="outlined"
            color="white"
          >
            {showMap ? "Ocultar mapa" : "Ver mapa"}
          </Button>
        )}
      </Box>

      <Collapse in={showPDF}>
        <Box
          component="iframe"
          src={pdfUrl}
          sx={{ width: "100%", height: 600, mt: 3 }}
        />
      </Collapse>

      <Collapse in={showMap}>
        <Box
          id={mapId}
          sx={{
            width: "100%",
            height: 500,
            mt: 3,
            borderRadius: 2,
            overflow: "hidden",
          }}
        />
      </Collapse>
    </Box>
  );
};

CountersGroup.propTypes = {
  title: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  municipios: PropTypes.arrayOf(
    PropTypes.shape({
      cantidad: PropTypes.number.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  pdf: PropTypes.shape({
    fields: PropTypes.shape({
      file: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
  coordenadas: PropTypes.arrayOf(
    PropTypes.shape({
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
      name: PropTypes.string,
    })
  ),
  mapId: PropTypes.string.isRequired, // Add mapId as a required prop
};

export default CountersGroup;
